import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  CheckCircle2,
  XCircle,
  Search,
  Store,
  Package,
  CreditCard,
  TrendingUp,
  ExternalLink,
  ChevronRight,
  Filter,
  RefreshCw,
  Loader2,
  Trash2,
  AlertTriangle,
  Wallet,
  BadgeCheck,
  Clock,
  Layers,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatIDR, fileUrl, downloadOrderInvoice, isUserAdmin } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AdminSettlements } from "@/components/AdminSettlements";

export default function Admin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("sellers"); // "sellers" | "orders" | "products" | "tarapay"
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);

  // Sellers tab state
  const [sellers, setSellers] = useState([]);
  const [sellerSearch, setSellerSearch] = useState("");
  const [sellerStatus, setSellerStatus] = useState("all");
  const [loadingSellers, setLoadingSellers] = useState(false);
  const [actionLoading, setActionLoading] = useState({});

  // Orders tab state
  const [orders, setOrders] = useState([]);
  const [orderStatus, setOrderStatus] = useState("all");
  const [orderSearch, setOrderSearch] = useState("");
  const [loadingOrders, setLoadingOrders] = useState(false);

  // Products tab state
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [productCat, setProductCat] = useState("all");
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Check admin access
  useEffect(() => {
    if (user && !user.is_admin && !isUserAdmin(user)) {
      toast.error("Akses ditolak: Khusus Admin");
      navigate("/");
    }
  }, [user, navigate]);

  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch (e) {
      toast.error("Gagal memuat statistik admin");
    } finally {
      setLoadingStats(false);
    }
  }, []);

  const loadSellers = useCallback(async () => {
    setLoadingSellers(true);
    try {
      const { data } = await api.get("/admin/sellers", {
        params: { q: sellerSearch, status: sellerStatus },
      });
      setSellers(data);
    } catch {
      toast.error("Gagal memuat daftar toko");
    } finally {
      setLoadingSellers(false);
    }
  }, [sellerSearch, sellerStatus]);

  const loadOrders = useCallback(async () => {
    setLoadingOrders(true);
    try {
      const { data } = await api.get("/admin/orders", {
        params: { q: orderSearch, status: orderStatus },
      });
      setOrders(data);
    } catch {
      toast.error("Gagal memuat transaksi");
    } finally {
      setLoadingOrders(false);
    }
  }, [orderSearch, orderStatus]);

  const loadProducts = useCallback(async () => {
    setLoadingProducts(true);
    try {
      const { data } = await api.get("/admin/products", {
        params: { q: productSearch, category: productCat },
      });
      setProducts(data);
    } catch {
      toast.error("Gagal memuat produk");
    } finally {
      setLoadingProducts(false);
    }
  }, [productSearch, productCat]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (tab === "sellers") loadSellers();
    else if (tab === "orders") loadOrders();
    else if (tab === "products") loadProducts();
  }, [tab, loadSellers, loadOrders, loadProducts]);

  const toggleVerify = async (sellerId, currentVerified) => {
    setActionLoading((prev) => ({ ...prev, [sellerId]: true }));
    const newStatus = !currentVerified;
    try {
      const { data } = await api.post(`/admin/sellers/${sellerId}/verify`, {
        verified: newStatus,
      });
      toast.success(
        newStatus
          ? `Toko ${data.user.name} berhasil diverifikasi!`
          : `Verifikasi toko ${data.user.name} dibatalkan.`
      );
      setSellers((prev) =>
        prev.map((s) => (s.id === sellerId ? { ...s, verified: newStatus } : s))
      );
      loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengubah status verifikasi");
    } finally {
      setActionLoading((prev) => ({ ...prev, [sellerId]: false }));
    }
  };

  const toggleProductStatus = async (productId, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    try {
      await api.post(`/admin/products/${productId}/toggle-status`, { status: newStatus });
      toast.success(`Status produk diubah menjadi ${newStatus}`);
      setProducts((prev) =>
        prev.map((p) => (p.id === productId ? { ...p, status: newStatus } : p))
      );
      loadStats();
    } catch {
      toast.error("Gagal mengubah status produk");
    }
  };

  const deleteProduct = async (productId, title) => {
    if (!window.confirm(`Hapus produk "${title}" secara permanen?`)) return;
    try {
      await api.delete(`/admin/products/${productId}`);
      toast.success("Produk berhasil dihapus");
      setProducts((prev) => prev.filter((p) => p.id !== productId));
      loadStats();
    } catch {
      toast.error("Gagal menghapus produk");
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-[#0B4DA2] via-[#125BBF] to-[#1D6FD1] rounded-3xl p-6 sm:p-8 text-white relative overflow-hidden shadow-lg">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 text-xs font-semibold backdrop-blur mb-2">
              <ShieldCheck className="w-3.5 h-3.5 text-sky-200" />
              <span>Portal Verifikasi & Manajemen Admin</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
              Dashboard Admin Kalabiru
            </h1>
            <p className="text-sm text-white/80 mt-1 max-w-xl">
              Verifikasi toko penjual, pantau transaksi QRIS secara real-time, dan moderasi katalog preloved.
            </p>
          </div>
          <button
            onClick={() => {
              loadStats();
              if (tab === "sellers") loadSellers();
              if (tab === "orders") loadOrders();
              if (tab === "products") loadProducts();
            }}
            className="self-start md:self-auto flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur text-sm font-semibold transition"
          >
            <RefreshCw className={`w-4 h-4 ${loadingStats ? "animate-spin" : ""}`} />
            Refresh Data
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between text-gray-500 mb-2">
            <span className="text-xs sm:text-sm font-medium">Toko Penjual</span>
            <div className="w-8 h-8 rounded-xl bg-blue-50 text-[#0B4DA2] flex items-center justify-center">
              <Store className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-extrabold text-gray-900">
            {stats ? stats.users.total : "..."}
          </div>
          <div className="text-xs text-emerald-600 font-medium mt-1 flex items-center gap-1">
            <BadgeCheck className="w-3.5 h-3.5" />
            <span>{stats ? stats.users.verified : 0} Terverifikasi</span>
            <span className="text-gray-400 font-normal">
              ({stats ? stats.users.unverified : 0} Belum)
            </span>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between text-gray-500 mb-2">
            <span className="text-xs sm:text-sm font-medium">Katalog Produk</span>
            <div className="w-8 h-8 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center">
              <Package className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-extrabold text-gray-900">
            {stats ? stats.products.total : "..."}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            <span className="text-[#0B4DA2] font-semibold">{stats ? stats.products.active : 0} Aktif</span> • {stats ? stats.products.sold : 0} Terjual
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between text-gray-500 mb-2">
            <span className="text-xs sm:text-sm font-medium">Total Transaksi</span>
            <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <TrendingUp className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-extrabold text-gray-900">
            {stats ? stats.orders.total : "..."}
          </div>
          <div className="text-xs text-emerald-600 font-medium mt-1 truncate">
            {stats ? formatIDR(stats.orders.total_volume) : "Rp 0"} lunas
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between text-gray-500 mb-2">
            <span className="text-xs sm:text-sm font-medium">Saldo Live</span>
            <div className="w-8 h-8 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
              <Wallet className="w-4 h-4" />
            </div>
          </div>
          <div className="text-xl sm:text-2xl font-extrabold text-[#0B4DA2] truncate" data-testid="admin-tarapay-balance">
            {stats?.tarapay ? formatIDR(Number(stats.tarapay.availableBalance)) : "..."}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            ID: {stats?.tarapay?.merchantId || "3000480"} (Production)
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="flex items-center gap-2 border-b border-gray-200 overflow-x-auto no-scrollbar">
        <button
          data-testid="admin-tab-sellers"
          onClick={() => setTab("sellers")}
          className={`flex items-center gap-2 px-5 py-3 font-semibold text-sm border-b-2 transition whitespace-nowrap ${
            tab === "sellers"
              ? "border-[#0B4DA2] text-[#0B4DA2]"
              : "border-transparent text-gray-500 hover:text-gray-800"
          }`}
        >
          <BadgeCheck className="w-4 h-4" />
          <span>Verifikasi Toko ({stats ? stats.users.total : 0})</span>
          {stats && stats.users.unverified > 0 && (
            <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] bg-amber-100 text-amber-800 font-bold">
              {stats.users.unverified} Butuh Verifikasi
            </span>
          )}
        </button>

        <button
          data-testid="admin-tab-orders"
          onClick={() => setTab("orders")}
          className={`flex items-center gap-2 px-5 py-3 font-semibold text-sm border-b-2 transition whitespace-nowrap ${
            tab === "orders"
              ? "border-[#0B4DA2] text-[#0B4DA2]"
              : "border-transparent text-gray-500 hover:text-gray-800"
          }`}
        >
          <CreditCard className="w-4 h-4" />
          <span>Transaksi & QRIS ({stats ? stats.orders.total : 0})</span>
        </button>

        <button
          data-testid="admin-tab-products"
          onClick={() => setTab("products")}
          className={`flex items-center gap-2 px-5 py-3 font-semibold text-sm border-b-2 transition whitespace-nowrap ${
            tab === "products"
              ? "border-[#0B4DA2] text-[#0B4DA2]"
              : "border-transparent text-gray-500 hover:text-gray-800"
          }`}
        >
          <Package className="w-4 h-4" />
          <span>Katalog Produk ({stats ? stats.products.total : 0})</span>
        </button>

        <button
          data-testid="admin-tab-tarapay"
          onClick={() => setTab("tarapay")}
          className={`flex items-center gap-2 px-5 py-3 font-semibold text-sm border-b-2 transition whitespace-nowrap ${
            tab === "tarapay"
              ? "border-[#0B4DA2] text-[#0B4DA2]"
              : "border-transparent text-gray-500 hover:text-gray-800"
          }`}
        >
          <Wallet className="w-4 h-4" />
          <span>Payment Gateway</span>
        </button>

        <button
          data-testid="admin-tab-settlement"
          onClick={() => setTab("settlement")}
          className={`flex items-center gap-2 px-5 py-3 font-semibold text-sm border-b-2 transition whitespace-nowrap ${
            tab === "settlement"
              ? "border-[#0B4DA2] text-[#0B4DA2]"
              : "border-transparent text-gray-500 hover:text-gray-800"
          }`}
        >
          <Layers className="w-4 h-4" />
          <span>Settlement Penarikan</span>
        </button>
      </div>

      {tab === "settlement" && <AdminSettlements />}

      {/* TAB 1: VERIFIKASI TOKO (SELLERS) */}
      {tab === "sellers" && (
        <div className="space-y-4">
          {/* Filters & Search */}
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="admin-seller-search"
                value={sellerSearch}
                onChange={(e) => setSellerSearch(e.target.value)}
                placeholder="Cari nama toko, nomor HP, atau kota..."
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#0B4DA2] bg-white"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <Filter className="w-3.5 h-3.5" /> Status:
              </span>
              <select
                data-testid="admin-seller-status-filter"
                value={sellerStatus}
                onChange={(e) => setSellerStatus(e.target.value)}
                className="px-3 py-2 rounded-xl border border-gray-200 text-sm bg-white outline-none focus:border-[#0B4DA2]"
              >
                <option value="all">Semua Toko</option>
                <option value="unverified">⏳ Belum Terverifikasi</option>
                <option value="verified">✅ Terverifikasi</option>
              </select>
            </div>
          </div>

          {/* Sellers List Table / Cards */}
          {loadingSellers ? (
            <div className="p-12 text-center text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-[#0B4DA2]" />
              Memuat daftar toko...
            </div>
          ) : sellers.length === 0 ? (
            <div className="p-12 text-center bg-white rounded-2xl border border-gray-100 text-gray-400">
              Tidak ada toko yang cocok dengan pencarian.
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm" data-testid="admin-sellers-table">
                  <thead className="bg-gray-50 text-gray-500 font-semibold text-xs border-b border-gray-100">
                    <tr>
                      <th className="p-4">Toko / Penjual</th>
                      <th className="p-4">Kontak & Lokasi</th>
                      <th className="p-4">Listing</th>
                      <th className="p-4">Saldo Dompet</th>
                      <th className="p-4">Status</th>
                      <th className="p-4 text-right">Aksi Verifikasi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sellers.map((s) => (
                      <tr key={s.id} className="hover:bg-gray-50/80 transition" data-testid={`seller-row-${s.id}`}>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <img
                              src={s.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${s.name}`}
                              alt=""
                              className="w-10 h-10 rounded-full object-cover border border-gray-100"
                            />
                            <div>
                              <div className="font-bold text-gray-900 flex items-center gap-1.5">
                                <span>{s.name}</span>
                                {s.verified && (
                                  <BadgeCheck className="w-4 h-4 text-[#0B4DA2] fill-sky-100" />
                                )}
                              </div>
                              <div className="text-xs text-gray-400 line-clamp-1 max-w-[200px]">
                                {s.bio || "Toko Preloved"}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td className="p-4">
                          <div className="font-medium text-gray-800">{s.phone}</div>
                          <div className="text-xs text-gray-400">{s.location || "Indonesia"}</div>
                        </td>

                        <td className="p-4">
                          <div className="text-xs text-gray-600">
                            <b className="text-gray-900">{s.active_count || 0}</b> aktif •{" "}
                            <span className="text-emerald-600 font-semibold">{s.sold_count || 0} terjual</span>
                          </div>
                        </td>

                        <td className="p-4">
                          <span className="font-bold text-[#0B4DA2]">
                            {formatIDR(s.wallet_balance || 0)}
                          </span>
                        </td>

                        <td className="p-4">
                          {s.verified ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                              <CheckCircle2 className="w-3.5 h-3.5" /> Terverifikasi
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                              <Clock className="w-3.5 h-3.5" /> Belum Terverifikasi
                            </span>
                          )}
                        </td>

                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => navigate(`/user/${s.id}`)}
                              className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
                              title="Lihat Profil Toko"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </button>

                            <button
                              data-testid={`verify-toggle-${s.id}`}
                              onClick={() => toggleVerify(s.id, s.verified)}
                              disabled={actionLoading[s.id]}
                              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 disabled:opacity-50 ${
                                s.verified
                                  ? "bg-gray-100 text-gray-700 hover:bg-red-50 hover:text-red-600"
                                  : "bg-[#0B4DA2] hover:bg-[#083B7C] text-white shadow-sm"
                              }`}
                            >
                              {actionLoading[s.id] ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : s.verified ? (
                                <>
                                  <XCircle className="w-3.5 h-3.5" />
                                  <span>Batalkan</span>
                                </>
                              ) : (
                                <>
                                  <ShieldCheck className="w-3.5 h-3.5" />
                                  <span>Verifikasi Toko</span>
                                </>
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 2: TRANSAKSI & QRIS */}
      {tab === "orders" && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="admin-order-search"
                value={orderSearch}
                onChange={(e) => setOrderSearch(e.target.value)}
                placeholder="Cari order ID atau judul produk..."
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#0B4DA2] bg-white"
              />
            </div>
            <select
              value={orderStatus}
              onChange={(e) => setOrderStatus(e.target.value)}
              className="px-3 py-2 rounded-xl border border-gray-200 text-sm bg-white outline-none focus:border-[#0B4DA2]"
            >
              <option value="all">Semua Status</option>
              <option value="paid">✅ Dibayar (Paid)</option>
              <option value="pending_payment">⏳ Menunggu Pembayaran</option>
            </select>
          </div>

          {loadingOrders ? (
            <div className="p-12 text-center text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-[#0B4DA2]" />
              Memuat transaksi...
            </div>
          ) : orders.length === 0 ? (
            <div className="p-12 text-center bg-white rounded-2xl border border-gray-100 text-gray-400">
              Belum ada riwayat transaksi.
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm" data-testid="admin-orders-table">
                  <thead className="bg-gray-50 text-gray-500 font-semibold text-xs border-b border-gray-100">
                    <tr>
                      <th className="p-4">Order ID & Tanggal</th>
                      <th className="p-4">Produk</th>
                      <th className="p-4">Pembeli & Penjual</th>
                      <th className="p-4">Total Nominal</th>
                      <th className="p-4">Metode</th>
                      <th className="p-4 text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {orders.map((o) => (
                      <tr key={o.id} className="hover:bg-gray-50/80 transition" data-testid={`order-row-${o.id}`}>
                        <td className="p-4">
                          <div className="font-mono font-bold text-gray-900 text-xs">
                            {o.mch_order_no || o.id.slice(0, 12)}
                          </div>
                          <div className="text-[11px] text-gray-400 mt-0.5">
                            {new Date(o.created_at).toLocaleString("id-ID")}
                          </div>
                        </td>

                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            {o.product_image && (
                              <img
                                src={fileUrl(o.product_image)}
                                alt=""
                                className="w-9 h-9 rounded-lg object-cover"
                              />
                            )}
                            <div className="font-medium text-gray-800 line-clamp-1 max-w-[180px]">
                              {o.product_title}
                            </div>
                          </div>
                        </td>

                        <td className="p-4">
                          <div className="text-xs">
                            <span className="text-gray-500">B: </span>
                            <span className="font-semibold text-gray-900">{o.buyer_name}</span>
                          </div>
                          <div className="text-xs mt-0.5">
                            <span className="text-gray-500">S: </span>
                            <span className="font-semibold text-gray-700">{o.seller_name}</span>
                          </div>
                        </td>

                        <td className="p-4">
                          <div className="font-bold text-[#0B4DA2]">{formatIDR(o.amount)}</div>
                          <div className="text-[10px] text-gray-400">Barang: {formatIDR(o.price)}</div>
                        </td>

                        <td className="p-4">
                          <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-blue-50 text-[#0B4DA2]">
                            QRIS
                          </span>
                        </td>

                        <td className="p-4 text-right">
                          <div className="flex flex-col items-end gap-1">
                            {o.status === "paid" ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                <CheckCircle2 className="w-3.5 h-3.5" /> Lunas
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
                                <Clock className="w-3.5 h-3.5" /> Menunggu Bayar
                              </span>
                            )}
                            <button
                              onClick={async () => {
                                try {
                                  await downloadOrderInvoice(o.id, o.mch_order_no);
                                  toast.success("Invoice pembelian berhasil diunduh");
                                } catch {
                                  toast.error("Gagal mengunduh invoice");
                                }
                              }}
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0B4DA2] hover:underline"
                              title="Unduh Invoice Pembelian (PDF)"
                            >
                              <FileText className="w-3 h-3" /> Invoice PDF
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 3: KATALOG PRODUK */}
      {tab === "products" && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="admin-product-search"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder="Cari judul barang preloved..."
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#0B4DA2] bg-white"
              />
            </div>
            <select
              value={productCat}
              onChange={(e) => setProductCat(e.target.value)}
              className="px-3 py-2 rounded-xl border border-gray-200 text-sm bg-white outline-none focus:border-[#0B4DA2]"
            >
              <option value="all">Semua Kategori</option>
              <option value="fashion">Fashion</option>
              <option value="elektronik">Elektronik</option>
              <option value="aksesoris">Aksesoris</option>
              <option value="rumah">Rumah Tangga</option>
              <option value="hobi">Hobi & Koleksi</option>
            </select>
          </div>

          {loadingProducts ? (
            <div className="p-12 text-center text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-[#0B4DA2]" />
              Memuat katalog...
            </div>
          ) : products.length === 0 ? (
            <div className="p-12 text-center bg-white rounded-2xl border border-gray-100 text-gray-400">
              Tidak ada produk yang cocok.
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm" data-testid="admin-products-table">
                  <thead className="bg-gray-50 text-gray-500 font-semibold text-xs border-b border-gray-100">
                    <tr>
                      <th className="p-4">Produk</th>
                      <th className="p-4">Penjual</th>
                      <th className="p-4">Kategori & Kondisi</th>
                      <th className="p-4">Harga</th>
                      <th className="p-4">Status</th>
                      <th className="p-4 text-right">Moderasi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {products.map((p) => (
                      <tr key={p.id} className="hover:bg-gray-50/80 transition" data-testid={`product-row-${p.id}`}>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <img
                              src={fileUrl((p.images || [])[0])}
                              alt=""
                              className="w-12 h-12 rounded-xl object-cover border border-gray-100 shrink-0"
                            />
                            <div className="min-w-0">
                              <div
                                onClick={() => navigate(`/product/${p.id}`)}
                                className="font-bold text-gray-900 hover:text-[#0B4DA2] cursor-pointer line-clamp-1 max-w-[220px]"
                              >
                                {p.title}
                              </div>
                              <div className="text-xs text-gray-400 mt-0.5">{p.location}</div>
                            </div>
                          </div>
                        </td>

                        <td className="p-4">
                          <div className="font-semibold text-gray-800 flex items-center gap-1">
                            <span>{p.seller?.name || "Penjual"}</span>
                            {p.seller?.verified && (
                              <BadgeCheck className="w-3.5 h-3.5 text-[#0B4DA2]" />
                            )}
                          </div>
                          <div className="text-xs text-gray-400">{p.seller?.location}</div>
                        </td>

                        <td className="p-4">
                          <span className="capitalize text-xs font-semibold px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                            {p.category}
                          </span>
                          <div className="text-xs text-gray-500 mt-1">{p.condition}</div>
                        </td>

                        <td className="p-4">
                          <div className="font-bold text-[#0B4DA2]">{formatIDR(p.price)}</div>
                        </td>

                        <td className="p-4">
                          <span
                            className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                              p.status === "active"
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                : p.status === "sold"
                                ? "bg-gray-100 text-gray-700"
                                : "bg-red-50 text-red-700 border border-red-200"
                            }`}
                          >
                            {p.status === "active"
                              ? "Aktif"
                              : p.status === "sold"
                              ? "Terjual"
                              : "Nonaktif"}
                          </span>
                        </td>

                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            {p.status !== "sold" && (
                              <button
                                onClick={() => toggleProductStatus(p.id, p.status)}
                                className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition ${
                                  p.status === "active"
                                    ? "bg-amber-50 text-amber-700 hover:bg-amber-100"
                                    : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                }`}
                              >
                                {p.status === "active" ? "Nonaktifkan" : "Aktifkan"}
                              </button>
                            )}
                            <button
                              onClick={() => deleteProduct(p.id, p.title)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                              title="Hapus Produk"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 4: TARAPAY GATEWAY */}
      {tab === "tarapay" && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm space-y-4">
            <div className="flex items-center justify-between pb-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-blue-50 text-[#0B4DA2] flex items-center justify-center">
                  <CreditCard className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-extrabold text-lg text-gray-900">
                    OpenAPI (Production)
                  </h3>
                  <div className="text-xs text-gray-500 mt-0.5">
                    Acquiring QRIS Dinamis & Disbursement Transfer Bank
                  </div>
                </div>
              </div>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> Live Terhubung
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                <div className="text-xs text-gray-500 font-medium">Merchant ID</div>
                <div className="text-lg font-bold text-gray-900 mt-1">
                  {stats?.tarapay?.merchantId || "3000480"}
                </div>
                <div className="text-[11px] text-gray-400 mt-0.5">App ID: mfshYoqTM41wYrKXYcl3lqjTR7tiC8GS</div>
              </div>

              <div className="p-4 rounded-xl bg-blue-50/50 border border-blue-100">
                <div className="text-xs text-[#0B4DA2] font-medium">Saldo Aktif (Siap Cair)</div>
                <div className="text-2xl font-extrabold text-[#0B4DA2] mt-1">
                  {stats?.tarapay ? formatIDR(Number(stats.tarapay.availableBalance)) : "..."}
                </div>
                <div className="text-[11px] text-gray-500 mt-0.5">
                  Saldo likuid untuk disbursement penjual
                </div>
              </div>

              <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                <div className="text-xs text-gray-500 font-medium">Pending Settlement (H+1)</div>
                <div className="text-xl font-bold text-gray-800 mt-1">
                  {stats?.tarapay ? formatIDR(Number(stats.tarapay.pendingSettlementAmount)) : "..."}
                </div>
                <div className="text-[11px] text-gray-400 mt-0.5">
                  Dalam proses kliring switching GPN/ASPI
                </div>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-amber-50/60 border border-amber-200 text-xs text-amber-900 space-y-1">
              <div className="font-bold flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4 text-amber-700" />
                Catatan Operasional Gateway:
              </div>
              <p>
                • Transaksi pay-in QRIS di-generate otomatis via Product Code 31 dan diverifikasi dengan tanda tangan kriptografi RSA-SHA256.
              </p>
              <p>
                • Penarikan dana oleh penjual langsung memicu API Disbursement ke rekening bank tujuan.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
